<template>
  <div :class="$style.base">
    <div v-if="pricing?.quote" :class="$style.wrapper">
      <div :class="$style.inner">
        <div :class="$style.top">
          <div
            v-if="!daysOfWeek.length"
            v-test="'summary-date'"
            :class="$style.date"
          >
            {{
              `${formattedDate} ${time ? $t('general.time_at') : ''} ${time}`
            }}
          </div>
          <div :class="$style.subRow">
            <div
              v-if="totalDuration && settings.show_service_duration"
              :class="$style.cell"
              v-test="'totalDuration'"
            >
              <IconClock />
              {{ totalDuration }} {{ $t('general.minute', totalDuration) }}
            </div>
            <div v-if="employee" :class="$style.cell" v-test="'resource'">
              <BaseAvatar :employee="employee" small />
              {{ employee.name }}
            </div>
          </div>
        </div>
        <div :class="$style.services" v-test="'summary-services'">
          <div
            v-for="(service, index) in pricing.quote.services"
            :key="index"
            :class="$style.row"
            v-test="'service-item'"
          >
            <div>{{ serviceById(service.id)?.name }}</div>
            <div :class="$style.price" v-if="showPrices" v-test="'price'">
              {{ currency(service.price) }}
            </div>
          </div>
        </div>
        <div :class="$style.totalSection">
          <div
            :class="$style.row"
            v-if="
              showPrices &&
              pricing.quote?.subtotal &&
              pricing.quote?.subtotal != pricing.quote?.total
            "
            v-test="'subtotalPrice'"
          >
            <div>{{ $t('general.subtotal') }}</div>
            <div :class="$style.price">
              {{ currency(pricing.quote.subtotal) }}
            </div>
          </div>
          <div
            :class="$style.row"
            v-if="
              showPrices &&
              pricing.quote?.discount?.adjustment_type ===
                'percentage_decrease' &&
              pricing.quote?.discount?.amount
            "
            v-test="'discount-price'"
          >
            <div>
              {{ $t('general.discount') }} ({{
                pricing.quote.discount?.percentage
              }}%)
            </div>
            <div :class="$style.price" v-test="'price-adjustment'">
              {{ currency(-pricing.quote.discount.amount) }}
            </div>
          </div>
          <div :class="$style.row" v-if="transactionFee" v-test="'payment-fee'">
            <div>{{ $t('confirmation.payment_fee') }}</div>
            <div :class="$style.price" v-test="'payment-fee-value'">
              {{ currency(transactionFee) }}
            </div>
          </div>
          <div
            :class="$style.row"
            v-if="giftcardRedeemedAmount"
            v-test="'redeemedAmount'"
          >
            <div>{{ $t('confirmation.giftcard_redeemed') }}</div>
            <div :class="$style.price" v-test="'redeemed-giftcard-value'">
              {{ currency(-giftcardRedeemedAmount) }}
            </div>
          </div>
          <div
            v-if="showPrices || paymentMayBeRequired"
            :class="[$style.row, $style.total]"
            v-test="'totalPrice'"
          >
            <div>{{ $t('general.total') }}</div>
            <div :class="$style.price">
              {{ currency(displayTotal) }}
            </div>
          </div>
        </div>
        <div>
          <DiscountCode v-if="showAddDiscount" />
          <GiftcardCode v-if="showAddGiftcard" />
        </div>
      </div>
      <div v-if="paymentMayBeRequired">
        <div
          :class="$style.extraInfo"
          :style="{
            backgroundColor: `${widgetSettings.color}1A`
          }"
          v-test="'conditional-payment-required-message'"
        >
          <div>
            {{
              $t(
                `confirmation.conditional_${
                  pricing.quote.payment_strategy === 'percentage'
                    ? 'deposit'
                    : 'payment'
                }`
              )
            }}
          </div>
        </div>
      </div>
      <div
        v-else-if="pricing.quote?.payment_strategy === 'required'"
        :class="$style.extraInfo"
        :style="{
          backgroundColor: `${widgetSettings.color}1A`
        }"
        v-test="'payment-required-message'"
      >
        <div>{{ $t('confirmation.complete_payment') }}</div>
      </div>
      <div
        v-else-if="
          pricing.quote?.payment_strategy !== 'disabled' &&
          pricing.quote?.deposit?.amount &&
          pricing.quote.deposit.amount - transactionFee > giftcardRedeemedAmount
        "
        :class="$style.extraInfo"
        :style="{
          backgroundColor: `${widgetSettings.color}1A`
        }"
        v-test="'deposit-required-message'"
      >
        <div :class="$style.row">
          <div v-if="transactionFee > 0" v-test="'deposit-confirmation'">
            {{ $t('confirmation.deposit') }} ({{
              pricing.quote.deposit.percentage
            }}%)
            {{ transactionFee ? ` + ${$t('confirmation.payment_fee')}` : '' }}
          </div>
          <div>
            {{ currency(pricing.quote.deposit.amount + transactionFee) }}
          </div>
        </div>
        <div :class="$style.row">{{ $t('confirmation.deposit_info') }}</div>
      </div>
    </div>
    <div v-else :class="$style.wrapper">
      <div :class="$style.inner">
        <div :class="$style.loaderContainer">
          <IconSpinner />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';

import { currency } from '@/helpers';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import { useEmployeesStore } from '@/stores/employees';
import { useServicesStore } from '@/stores/services';
import widgetSettings from '@/widget-settings';

import BaseAvatar from '@/components/BaseAvatar.vue';
import IconClock from '@/components/IconClock.vue';
import IconSpinner from '@/components/IconSpinner.vue';
import DiscountCode from './DiscountCode.vue';
import GiftcardCode from './GiftcardCode.vue';

const {
  date,
  time,
  employeeId,
  totalDuration,
  timeSlotPriceAdjustment,
  waitingList,
  daysOfWeek
} = useBookingStore();

const submittedEmail = inject<Ref<string>>('submittedEmail')!;
const { pricing, paymentMethod, giftcardRedeemedAmount } = storeToRefs(
  useBookingStore()
);
const { serviceById } = useServicesStore();
const { company, settings } = useCompanyStore();

const showPrices = computed(
  () =>
    settings.show_service_prices ||
    pricing.value?.quote?.payment_strategy !== 'disabled'
);

const paymentMayBeRequired = computed(
  () =>
    company.settings.prepayments_for_new_customers_only &&
    (pricing.value?.quote?.payment_strategy === 'required' ||
      pricing.value?.quote?.payment_strategy === 'percentage') &&
    !submittedEmail.value
);

const showAddDiscount = computed(
  () =>
    pricing.value?.quote?.total &&
    !timeSlotPriceAdjustment &&
    company.settings.discount_codes_enabled &&
    !waitingList
);

const showAddGiftcard = computed(
  () =>
    company.settings.online_payment !== 'disabled' &&
    pricing.value?.quote?.total &&
    company.settings.giftcard_payments_allowed &&
    !waitingList
);

const { d } = useI18n();
const formattedDate = computed(() =>
  date ? d(new Date(`${date}T00:00:00`), 'full') : null
);

const employee = computed(() => {
  const { employees } = useEmployeesStore();
  return employees.find((employee) => employee.id === employeeId);
});

const transactionFee = computed(() =>
  paymentMethod.value === 'pay_online' &&
  pricing.value &&
  giftcardRedeemedAmount.value !== pricing.value.quote.total
    ? pricing.value.quote.transaction_fee
    : 0
);

const displayTotal = computed(() =>
  pricing.value && paymentMethod.value === 'pay_online'
    ? pricing.value.quote.total +
      transactionFee.value -
      giftcardRedeemedAmount.value
    : pricing.value?.quote.total
      ? pricing.value.quote.total - giftcardRedeemedAmount.value
      : 0
);
</script>

<style lang="scss" module>
.base {
  margin-top: $spacing * 0.5;
  margin-bottom: $spacing * 2;
}

.wrapper {
  border: 1px solid $color-border-input;
  border-radius: 8px;
  background-color: white;
}

.inner {
  padding: $spacing;
}

.extraInfo {
  padding: $spacing;
  border-radius: 0 0 8px 8px;
}

.loaderContainer {
  position: relative;
  padding: 8px;
}

.summary-date {
  text-transform: capitalize;
}

.category {
  &:not(:first-of-type) {
    margin-top: $spacing;
  }
}

.row {
  display: flex;
  justify-content: space-between;
  &:not(:first-child) {
    margin: $spacing 0 0;
  }
}

.cell {
  display: flex;
  align-items: center;

  & > * {
    margin-right: 4px;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  &:not(:last-child) {
    margin-right: $spacing;
  }
}

.top {
  padding-bottom: $spacing;
}
.date {
  font-size: 18px;
  font-weight: bold;
}

.price {
  padding-left: $spacing;
}

.services {
  padding: $spacing 0;
  border-top: 1px solid $color-border;
}

.total {
  font-weight: bold;
}

.totalSection {
  padding: $spacing 0 0;
  border-top: 1px solid $color-border;
}

.subRow {
  display: flex;
  font-size: 14px;
  @include font-sub;

  &:not(:first-child) {
    margin-top: $spacing * 0.5;
  }

  & > * {
    margin-right: 4px;
  }

  &:empty {
    display: none;
  }
}
.bottom {
  margin-top: $spacing;
  padding-top: $spacing;
  border-top: 1px solid $color-border;
}
</style>
